<template>
  <div class="fade-in p-3 sm:p-4 max-w-screen-xl w-full mx-auto">
    <div class="h-full">
      <div
        class="flex flex-col sm:flex-row sm:items-center mb-4 sm:mb-2 space-y-3 sm:space-y-0 sm:space-x-2 justify-between"
      >
        <div class="flex items-center">
          <Briefcase class="w-5 h-5 mr-2 text-gray-700" />
          <div>
            <h1 class="text-xl font-normal text-gray-800 min-w-[120px]">
              {{ expeditionName }} Job Applications
            </h1>
          </div>
        </div>
        <p class="text-sm ml-auto text-gray-500 flex items-center gap-1">
          <Calendar class="w-4 h-4" />
          <span>Batch started</span>
          <span class="font-medium">{{ formatDateSafe(stats?.expedition_start_date) }}</span>
        </p>
      </div>

      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div class="bg-white rounded-xl border border-gray-200 px-4 py-3">
          <div class="text-xs uppercase tracking-wide text-gray-500">Total Fellows</div>
          <div class="text-2xl text-gray-900">{{ stats?.total_users ?? 0 }}</div>
        </div>
        <div class="bg-white rounded-xl border border-gray-200 px-4 py-3">
          <div class="text-xs uppercase tracking-wide text-gray-500">Applications</div>
          <div class="text-2xl text-gray-900">{{ totalApplications }}</div>
        </div>
        <div class="bg-white rounded-xl border border-gray-200 px-4 py-3">
          <div class="text-xs uppercase tracking-wide text-gray-500">Referrals</div>
          <div class="text-2xl text-gray-900">{{ totalSuccessfulReferrals }}</div>
        </div>
        <div class="relative bg-white rounded-xl border border-gray-200 px-4 py-3 overflow-hidden">
          <ShineBorder
            v-if="totalOffers > 0"
            :border-width="1"
            :duration="8"
            :shine-color="['#A07CFE', '#FE8FB5', '#FFBE7B']"
          />
          <div class="text-xs uppercase tracking-wide text-gray-500">Offers</div>
          <div class="text-2xl text-gray-900">{{ totalOffers }}</div>
        </div>
      </div>

      <div
        class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto overflow-y-hidden"
      >
        <div v-if="isLoading" class="flex items-center justify-center py-20">
          <OuttalentLoader />
        </div>
        <div v-else-if="error" class="flex flex-col items-center justify-center py-16 text-center">
          <div class="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle class="w-7 h-7 text-red-400" />
          </div>
          <h3 class="text-base font-medium text-gray-900 mb-1">Unable to load statistics</h3>
          <p class="text-sm text-gray-500">{{ error }}</p>
        </div>
        <div v-else-if="users.length > 0" class="w-full">
          <table class="w-full border-collapse min-w-[1100px]">
            <thead>
              <tr class="border-b border-gray-200">
                <th
                  class="text-left py-4 px-6 font-medium text-xs uppercase tracking-wider text-gray-500 min-w-[260px]"
                >
                  Adventurer
                </th>
                <th
                  class="text-left py-4 px-4 font-medium text-xs uppercase tracking-wider text-gray-500"
                >
                  Status & Latest Application
                </th>
                <th
                  class="text-center py-4 px-4 font-medium text-xs uppercase tracking-wider text-gray-500"
                >
                  Applications
                </th>
                <th
                  class="text-center py-4 px-4 font-medium text-xs uppercase tracking-wider text-gray-500"
                >
                  Referrals
                </th>
                <th
                  class="text-left py-4 px-4 font-medium text-xs uppercase tracking-wider text-gray-500"
                >
                  First App
                </th>
                <th
                  class="text-left py-4 px-4 font-medium text-xs uppercase tracking-wider text-gray-500"
                ></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr
                v-for="user in users"
                :key="user.user_id"
                class="hover:bg-gray-50/50 transition-colors"
              >
                <td class="py-4 px-6 min-w-[260px]">
                  <div class="flex items-center gap-3">
                    <component
                      :is="isBlurred ? 'div' : 'a'"
                      :href="isBlurred ? undefined : `https://outtalent.com/profile/${getUserUsername(user)}`"
                      :target="isBlurred ? undefined : '_blank'"
                      :rel="isBlurred ? undefined : 'noopener noreferrer'"
                      class="relative flex-shrink-0"
                    >
                      <div
                        v-if="getAvatarUrl(user)"
                        class="w-9 h-9 rounded-full ring-2 ring-white shadow-sm hover:ring-gray-200 transition-all overflow-hidden"
                      >
                        <img
                          :src="getAvatarUrl(user) || ''"
                          :alt="getUserName(user)"
                          class="w-full h-full object-cover"
                          :class="{ 'blur-md select-none': isBlurred }"
                        />
                      </div>
                      <div
                        v-else
                        class="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center ring-2 ring-white shadow-sm hover:ring-gray-200 transition-all overflow-hidden"
                      >
                        <div class="w-full h-full flex items-center justify-center" :class="{ 'blur-md select-none': isBlurred }">
                          <User class="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    </component>
                    <div class="flex flex-col">
                      <span class="text-sm font-medium text-gray-900" :class="{ 'blur-md select-none': isBlurred }">{{ getUserName(user) }}</span>
                      <span class="text-xs text-gray-500" :class="{ 'blur-md select-none': isBlurred }">{{ getUserEmail(user) }}</span>
                      <div class="flex items-center gap-2 text-[11px] text-gray-500 mt-1">
                        <a
                          :href="isBlurred ? undefined : `https://interviewwatch.outtalent.com/user/${user.user_details?.username}`"
                          target="_blank"
                          rel="noopener noreferrer"
                          v-if="(user.total_interview_logs ?? 0) > 0"
                          class="text-blue-700 px-2 py-1 rounded-full bg-blue-100"
                        >
                          {{ user.total_interview_logs }}
                          {{ user.total_interview_logs === 1 ? 'Interview' : 'Interviews' }}
                      </a>
                        <span
                          v-if="(user.total_offers ?? 0) > 0"
                          class="relative inline-flex rounded-full overflow-hidden"
                        >
                          <ShineBorder
                            :border-width="1"
                            :duration="8"
                            :shine-color="['#A07CFE', '#FE8FB5', '#FFBE7B']"
                          />
                          <a
                            :href="isBlurred ? undefined : `https://offers.outtalent.com/?search=${user.user_details?.first_name}+${user.user_details?.last_name}`"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="relative z-10 rounded-full px-2 py-1 flex items-center text-amber-600 bg-amber-400/10"
                          >
                            <Trophy class="w-3 h-3 mr-1" />
                            {{ user.total_offers }}
                            {{ user.total_offers === 1 ? 'Offer' : 'Offers' }}
                    </a>
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
                <td class="py-4 px-4">
                  <div class="flex flex-col gap-1">
                    <span
                      class="text-xs font-medium px-2 py-1 rounded-full w-fit"
                      :class="getStatusClass(user.job_application_status)"
                    >
                      <Trophy
                        v-if="user.job_application_status === 'signed_offer'"
                        class="w-3 h-3 mr-0.5 mb-0.5 inline-block"
                      />
                      {{ formatStatusLabel(user.job_application_status) }}
                    </span>
                    <span
                      v-if="
                        user.latest_application_created_at &&
                        user.job_application_status &&
                        user.job_application_status !== 'signed_offer'
                      "
                      class="text-xs px-2 py-1 rounded-full w-fit border-[1px] border-gray-200 text-gray-500"
                    >
                      {{ formatDateSafe(user.latest_application_created_at) }}
                    </span>
                  </div>
                </td>
                <td class="py-4 px-4 text-sm text-gray-700 text-balance text-center">
                  <a
                    v-if="!isBlurred"
                    :href="`https://tracktalent.outtalent.com/applicant/${getUserId(user)}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="underline decoration-dotted underline-offset-2 text-gray-700 hover:text-cosmicPurple transition-colors"
                  >
                    {{ getTotalApplications(user) }}
                  </a>
                  <span v-else>{{ getTotalApplications(user) }}</span>
                </td>
                <td class="py-4 px-4 text-sm text-gray-700 text-balance text-center">
                  {{ user.total_successful_referrals_count ?? 0 }}
                </td>
                <td class="py-4 px-4 text-xs text-gray-600 text-balance max-w-[120px]">
                  {{ formatDateSafe(user.first_application_created_at) }}
                </td>
                <td class="py-0 px-0 text-xs text-gray-600 text-balance">
                  <a
                    v-if="!isBlurred"
                    :href="`http://localhost:5173/manage/fellow/${getUserId(user)}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="h-full w-full flex items-center justify-center py-4 px-4 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    <span class="sr-only">Open fellow profile</span>
                    <ChevronRight class="w-4 h-4" />
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="flex flex-col items-center justify-center py-20 text-center">
          <div class="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
            <Briefcase class="w-7 h-7 text-gray-300" />
          </div>
          <h3 class="text-base font-medium text-gray-900 mb-1">No job applications yet</h3>
          <p class="text-sm text-gray-500">There is no activity for this expedition yet.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { AlertTriangle, Briefcase, User, Trophy, ChevronRight, Calendar } from 'lucide-vue-next'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import OuttalentLoader from '@/components/common/OuttalentLoader.vue'
import ShineBorder from '@/components/ui/ShineBorder.vue'
import { formatExpeditionName } from '@/utils/formatExpeditionName'
import { formatDateWithDaysAgo } from '@/utils/formatDate'
import type {
  ExpeditionJobApplicationStats,
  ExpeditionJobApplicationUser
} from '@/types/expedition'
import { usePrivacyMode } from '@/composables/usePrivacyMode'

const route = useRoute()
const { isBlurred } = usePrivacyMode()

const isLoading = ref(true)
const error = ref<string | null>(null)
const stats = ref<ExpeditionJobApplicationStats | null>(null)

const expeditionId = computed(() => route.params.id as string)
const users = computed(() => stats.value?.users ?? [])

const expeditionName = computed(() => {
  const name = stats.value?.expedition_name || ''
  return formatExpeditionName(name) || `Expedition ${expeditionId.value}`
})

const totalApplications = computed(() =>
  users.value.reduce((sum, user) => sum + (user.total_applications_count ?? 0), 0)
)
const totalSuccessfulReferrals = computed(() =>
  users.value.reduce((sum, user) => sum + (user.total_successful_referrals_count ?? 0), 0)
)
const totalOffers = computed(() =>
  users.value.reduce((sum, user) => sum + (user.total_offers ?? 0), 0)
)

const formatDateSafe = (date: string | null | undefined) => {
  if (!date) return '—'
  return formatDateWithDaysAgo(date, undefined, true)
}

const formatStatusLabel = (status: string) => {
  if (!status) return 'Unknown'
  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const getStatusClass = (status: string) => {
  const normalized = status?.toLowerCase()
  if (normalized === 'actively_applying') return 'bg-emerald-50 text-emerald-700'
  if (normalized === 'stopped_applying') return 'bg-rose-50 text-rose-700'
  if (normalized === 'signed_offer') return 'bg-cosmicPurple/10 text-indigo-500'
  if (normalized === 'paused_applying') return 'bg-amber-50 text-amber-700'
  return 'bg-gray-100 text-gray-600'
}

const getAvatarUrl = (user: ExpeditionJobApplicationUser) => {
  return user.user_details?.avatar_image || null
}

const getUserId = (user: ExpeditionJobApplicationUser) => {
  return user.user_details?.id || user.user_id
}

const getUserUsername = (user: ExpeditionJobApplicationUser) => {
  return user.user_details?.username || ''
}

const getUserName = (user: ExpeditionJobApplicationUser) => {
  const firstName = user.user_details?.first_name?.trim()
  const lastName = user.user_details?.last_name?.trim()
  const fullName = [firstName, lastName].filter(Boolean).join(' ')
  return fullName || user.user_details?.email || user.user_email || user.user_name || 'Unknown'
}

const getUserEmail = (user: ExpeditionJobApplicationUser) => {
  return user.user_details?.email || user.user_email || user.user_name || '—'
}

const getTotalApplications = (user: ExpeditionJobApplicationUser) => {
  return user.total_applications_count ?? user.total_applications ?? 0
}

const fetchData = async () => {
  isLoading.value = true
  error.value = null
  try {
    const response = await api.get(
      API_ROUTES.GET_EXPEDITION_JOB_APPLICATION_STATISTICS(expeditionId.value)
    )
    stats.value = response.data
  } catch (err) {
    console.error('Error fetching expedition job applications:', err)
    stats.value = null
    error.value = 'Please try again or refresh the page.'
  } finally {
    isLoading.value = false
  }
}

onMounted(fetchData)

watch(
  () => route.params.id,
  () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchData()
  }
)
</script>
